// FamilyPills.tsx — the family a row belongs to, as a pill that filters.
//
// Needs you and Work list things from every family on one page. Each row wears
// its family at the far left; pressing the pill keeps that family's rows, and
// pressing it again (or "All" in the strip above the list) shows every family.
// One look and one behaviour on both pages.

import { Link } from 'react-router-dom';

import { sameFamily } from './familyScope';

import './FamilyPills.css';

export interface FamilyCountEntry {
  family: string;
  /** Left out by a page that has no number to put on the pill. */
  count?: number;
}

/** The pill at the far left of a row. `picked` is the active filter ('' = all). */
export function FamilyPill({
  family,
  picked,
  onPick,
  className,
}: {
  family: string;
  picked: string;
  onPick: (family: string) => void;
  className?: string;
}): JSX.Element {
  // `acme` and `acme-split` are one family: a row filed under either name
  // reads as picked while that family is the scope.
  const active = sameFamily(picked, family) && picked !== '';
  return (
    <button
      type="button"
      className={`family-pill family-pill--row${className ? ` ${className}` : ''}`}
      aria-pressed={active}
      aria-label={active ? `Showing only ${family}: show every family` : `Show only ${family}`}
      title={active ? 'Show every family' : `Show only ${family}`}
      onClick={() => onPick(family)}
    >
      {family}
    </button>
  );
}

/**
 * One pill per family where exactly one is always shown (no "All"): the
 * release board picks the family whose board it draws. With `hrefOf` each
 * pill is a link to that family's address (the picked one is the current
 * page) and `onPick` still runs on the click, e.g. to remember the pick.
 */
export function FamilyPicker({
  families,
  family,
  onPick,
  label,
  hrefOf,
}: {
  families: string[];
  family: string;
  onPick: (family: string) => void;
  label: string;
  hrefOf?: (family: string) => string;
}): JSX.Element {
  return (
    <div className="family-strip" role="group" aria-label={label}>
      {families.map((entry) =>
        hrefOf ? (
          <Link
            key={entry}
            to={hrefOf(entry)}
            className="family-pill"
            aria-current={sameFamily(family, entry) ? 'page' : undefined}
            onClick={() => onPick(entry)}
          >
            {entry}
          </Link>
        ) : (
        <button
          key={entry}
          type="button"
          className="family-pill"
          aria-pressed={sameFamily(family, entry)}
          onClick={() => onPick(entry)}
        >
          {entry}
        </button>
        )
      )}
    </div>
  );
}

/**
 * "All" plus one pill per family, each with its count. A page that knows no
 * counts leaves them out and gets the same strip with bare names on it.
 */
export function FamilyStrip({
  counts,
  family,
  onPick,
}: {
  counts: FamilyCountEntry[];
  family: string;
  onPick: (family: string) => void;
}): JSX.Element {
  const counted = counts.every((entry) => entry.count !== undefined);
  const total = counted ? counts.reduce((sum, entry) => sum + (entry.count ?? 0), 0) : null;
  return (
    <div className="family-strip" role="group" aria-label="Filter by family">
      <button
        type="button"
        className="family-pill"
        aria-pressed={family === ''}
        onClick={() => onPick('')}
      >
        All{total === null ? null : <> <span className="family-pill__count">{total}</span></>}
      </button>
      {counts.map((entry) => (
        <button
          key={entry.family}
          type="button"
          className="family-pill"
          aria-pressed={sameFamily(family, entry.family) && family !== ''}
          onClick={() => onPick(entry.family)}
        >
          {entry.family}
          {entry.count === undefined ? null : (
            <> <span className="family-pill__count">{entry.count}</span></>
          )}
        </button>
      ))}
    </div>
  );
}
